import { Navigate, Route } from 'react-router-dom';
import { WAREHOUSE_HOME } from '../../layouts/areaSwitch';
import { AdminLayout } from '../../layouts/AdminLayout';
import { WarehouseScope } from '../../layouts/WarehouseScope';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Deliveries } from './pages/Deliveries/Deliveries';
import { DeliveriesAttention } from './pages/DeliveriesAttention/DeliveriesAttention';
import { Pickups } from './pages/Pickups';
import { Upload } from './pages/Upload/Upload';
import { Account } from '../../pages/Account/Account';
import { Sellers } from './pages/Sellers';
import { CreateSeller } from './pages/Sellers/CreateSeller';
import { SellerDetail } from './pages/SellerDetail';
import { Partnerships } from './pages/Partnerships/Partnerships';
import { IncomingUpsells } from './pages/IncomingUpsells/IncomingUpsells';
import { Couriers } from './pages/Couriers/Couriers';
import { ExternalIntegrations } from './pages/ExternalIntegrations/ExternalIntegrations';
import { OrderDetails } from './pages/OrderDetails/OrderDetails';
import { CreatePickupRequest } from './pages/PickupRequests/CreatePickupRequest';
import { AssignToManifest } from './pages/PickupRequests/AssignToManifest';
import { Inventory } from './pages/Inventory';
import { InventoryKardex } from './pages/InventoryKardex';
import { Movements } from './pages/Movements';
import { InventoryShopify } from './pages/InventoryShopify';
import { InventoryShopifySync } from './pages/InventoryShopifySync';
import { InventoryShopifySales } from './pages/InventoryShopifySales';
import { Locations } from './pages/Locations';
import { WarehouseClients } from './pages/WarehouseClients';
import { PickupPoints } from './pages/PickupPoints';
import { Products } from './pages/Products';
import { Putaway } from './pages/Putaway';
import {
  WarehouseOrderDetail,
  WarehouseOrderFulfill,
  WarehouseOrders,
} from './pages/WarehouseOrders';
import { ReceiptDetail, Receipts } from './pages/Receipts';
import { Supplies, SupplyDetail } from './pages/Supplies';
import { NotFound } from '../../pages/NotFound/NotFound';
import { UPSELLING_HIDDEN } from '../../features/upselling/upsellingConstants';

/**
 * Mounted by `AppRouter` when the session is an admin's. Elements rather than a
 * component, because `<Routes>` only accepts `<Route>` children.
 */
export const adminRoutes = (
  <Route element={<AdminLayout />}>
    <Route path="/home" element={<Navigate to="/dashboard" replace />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/deliveries" element={<Deliveries />} />
    <Route path="/deliveries/attention" element={<DeliveriesAttention />} />
    <Route path="/order-details/:orderId" element={<OrderDetails />} />
    <Route path="/pickups" element={<Pickups />} />
    <Route path="/upload" element={<Upload />} />
    <Route path="/account" element={<Account />} />
    <Route path="/sellers" element={<Sellers />} />
    <Route path="/sellers/new" element={<CreateSeller />} />
    <Route path="/sellers/:companyId" element={<SellerDetail />} />
    {/* Upselling is hidden for now: its addresses go with its menu entries, or
        they stay reachable by typing them. See `UPSELLING_HIDDEN`. */}
    {!UPSELLING_HIDDEN && <Route path="/partnerships" element={<Partnerships />} />}
    {!UPSELLING_HIDDEN && <Route path="/incoming-upsells" element={<IncomingUpsells />} />}
    <Route path="/my-providers-credentials" element={<Couriers />} />
    <Route path="/external-integrations" element={<ExternalIntegrations />} />
    {/* `/shipping-zones` is not served to an admin on purpose: the section is
        built but not being handed to customers yet. The route goes with the
        sidebar link, or the address stays reachable by typing it. The staff
        tree keeps both. */}
    <Route path="/pickup-requests/create" element={<CreatePickupRequest />} />
    <Route path="/pickup-requests/assign" element={<AssignToManifest />} />
    {/* Pickup points answer for their own addon inside the page: without it the
        address is the "page does not exist" screen, like the sidebar entry. */}
    <Route path="/pickup-points" element={<PickupPoints />} />
    {/* The warehouse module. `WarehouseScope` is what answers for the addon:
        without it these addresses are the "page does not exist" screen, the
        same way the sidebar leaves its entry out. Under them `AdminLayout`
        swaps the sidebar for the module's own menu. */}
    <Route element={<WarehouseScope />}>
      {/* The module opens on the orders: the bare address lands there too. */}
      <Route path="/warehouse" element={<Navigate to={WAREHOUSE_HOME} replace />} />
      <Route path="/warehouse/orders" element={<WarehouseOrders />} />
      <Route path="/warehouse/orders/:orderId" element={<WarehouseOrderDetail />} />
      <Route path="/warehouse/orders/:orderId/fulfill" element={<WarehouseOrderFulfill />} />
      <Route path="/warehouse/receipts" element={<Receipts />} />
      <Route path="/warehouse/receipts/:receiptId" element={<ReceiptDetail />} />
      <Route path="/warehouse/putaway" element={<Putaway />} />
      <Route path="/warehouse/inventory" element={<Inventory />} />
      <Route path="/warehouse/inventory/shopify" element={<InventoryShopify />} />
      <Route path="/warehouse/inventory/shopify/sync" element={<InventoryShopifySync />} />
      <Route path="/warehouse/inventory/shopify/sales" element={<InventoryShopifySales />} />
      <Route path="/warehouse/inventory/:productId" element={<InventoryKardex />} />
      <Route path="/warehouse/movements" element={<Movements />} />
      <Route path="/warehouse/products" element={<Products />} />
      <Route path="/warehouse/supplies" element={<Supplies />} />
      <Route path="/warehouse/supplies/:supplyId" element={<SupplyDetail />} />
      <Route path="/warehouse/locations" element={<Locations />} />
      <Route path="/warehouse/clients" element={<WarehouseClients />} />
    </Route>
    {/* Inside the layout on purpose: a wrong address is not a reason to
        take the sidebar away from someone who is signed in. */}
    <Route path="*" element={<NotFound />} />
  </Route>
);
